import { documentToFile, type CoasterDocument } from '../doc/document';
import type { CoasterFile } from '../doc/schema';
import type { SupportResult } from './types';
import { deserializeSupportResult } from './serialize';
import type { SupportWorkerRequest, SupportWorkerResponse } from './supportWorker';

type Pending = {
  resolve: (result: SupportResult) => void;
  reject: (err: Error) => void;
};

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./supportWorker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (ev: MessageEvent<SupportWorkerResponse>) => {
    const msg = ev.data;
    const slot = pending.get(msg.id);
    if (!slot) return;
    pending.delete(msg.id);
    if (msg.ok) slot.resolve(deserializeSupportResult(msg.result));
    else slot.reject(new Error(msg.error));
  };
  worker.onerror = (ev) => {
    const err = new Error(ev.message || 'Support worker failed');
    for (const slot of pending.values()) slot.reject(err);
    pending.clear();
    worker?.terminate();
    worker = null;
  };
  return worker;
}

function asCoasterFile(docOrFile: CoasterDocument | CoasterFile): CoasterFile {
  if ('format' in docOrFile) return docOrFile;
  return documentToFile(docOrFile);
}

/** Run {@link generateSupports} off the main thread so the sim/render loop stays responsive. */
export function generateSupportsInBackground(
  docOrFile: CoasterDocument | CoasterFile,
): Promise<SupportResult> {
  const file = asCoasterFile(docOrFile);
  const id = nextId++;
  const request: SupportWorkerRequest = { id, file };

  return new Promise<SupportResult>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    try {
      ensureWorker().postMessage(request);
    } catch (err) {
      pending.delete(id);
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}
